/**
 * Customer Rewards — Pass B (Rich UX).
 *
 * Layout:
 *   < md  : single-column list with rich cards.
 *   >= md : 3-col grid grouped by tier.
 *
 * Cards have two states:
 *   eligible    → photo + name + cost + REDEEM button
 *   ineligible  → photo with lock overlay + progress bar +
 *                 "X pts to go" or "Reach {Tier}"
 *
 * NOTE: Backend redemption endpoint ships in Session 4 (staff scanner).
 * For Pass B we surface a sonner toast instructing the guest to show their
 * QR. No state mutation. No fake confetti.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { SkeletonBlock } from "../../components/Skeleton";
import { tierLabel, tierTagline, TIER_ORDER, tierIndex } from "../../lib/tier";

const ProgressBar = ({ percent }) => (
  <div
    className="h-1.5 w-full bg-napoli-cream-warm overflow-hidden"
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.round(percent)}
  >
    <div
      className="h-full bg-napoli-gold transition-[width] duration-700 ease-out"
      style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
    />
  </div>
);

const RewardCard = ({ reward, userTier, onRedeem }) => {
  const eligible = !!reward.eligible;
  const reasonTier = reward.reason_ineligible === "tier_locked";
  const pct = Math.max(0, Math.min(100, reward.progress_to_eligible || 0));

  return (
    <article
      data-testid={`reward-card-${reward.id}`}
      data-eligible={eligible ? "yes" : "no"}
      className={[
        "group relative bg-napoli-marble border border-napoli-gold/30 flex flex-col",
        "transition-all duration-200",
        eligible
          ? "hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(31,22,17,0.35)] hover:border-napoli-gold/60"
          : "",
      ].join(" ")}
    >
      {reward.image_url ? (
        <div
          className={[
            "relative h-44 w-full bg-cover bg-center",
            !eligible && "grayscale-[40%] brightness-90",
          ].join(" ")}
          style={{ backgroundImage: `url(${reward.image_url})` }}
          aria-hidden
        >
          {!eligible ? (
            <span className="absolute inset-0 bg-napoli-espresso/35 flex items-center justify-center">
              <span
                data-testid={`reward-lock-${reward.id}`}
                className="h-12 w-12 rounded-full bg-napoli-espresso/80 flex items-center justify-center"
              >
                <Lock size={20} className="text-napoli-cream" />
              </span>
            </span>
          ) : null}
          <span className="absolute top-3 left-3 inline-flex items-center px-2 py-0.5 bg-napoli-marble/95 font-body text-[10px] tracking-[0.18em] uppercase text-napoli-oxblood">
            {reward.points_cost} pts
          </span>
        </div>
      ) : (
        <div className="relative px-5 pt-5 flex items-center justify-between">
          <span className="inline-flex items-center px-2 py-0.5 bg-napoli-cream-warm font-body text-[10px] tracking-[0.18em] uppercase text-napoli-oxblood border border-napoli-gold/30">
            {reward.points_cost} pts
          </span>
          {!eligible ? (
            <span
              data-testid={`reward-lock-${reward.id}`}
              className="h-9 w-9 rounded-full bg-napoli-espresso/80 flex items-center justify-center"
            >
              <Lock size={16} className="text-napoli-cream" />
            </span>
          ) : null}
        </div>
      )}

      <div className="flex-1 p-5 flex flex-col">
        <p className="font-display font-semibold text-base text-napoli-espresso leading-tight">
          {reward.name}
        </p>
        <p className="mt-1 font-body text-[12px] text-napoli-espresso-soft line-clamp-2">
          {reward.description}
        </p>

        <div className="mt-auto pt-4">
          {eligible ? (
            <button
              type="button"
              onClick={() => onRedeem(reward)}
              data-testid={`reward-redeem-${reward.id}`}
              className="w-full inline-flex items-center justify-center gap-2 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.18em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors"
            >
              Redeem <ArrowRight size={14} />
            </button>
          ) : reasonTier ? (
            <div data-testid={`reward-locked-${reward.id}`}>
              <p className="font-body text-[11px] tracking-[0.2em] uppercase text-napoli-espresso-soft">
                Reach <span className="text-napoli-oxblood">{tierLabel(reward.tier_required)}</span> tier
              </p>
              <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft/80">
                {tierTagline(reward.tier_required)}
              </p>
            </div>
          ) : (
            <div data-testid={`reward-progress-${reward.id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-body text-[11px] tracking-[0.2em] uppercase text-napoli-espresso-soft">
                  {reward.points_short} pts to go
                </span>
                <span className="font-body text-[11px] text-napoli-oxblood">
                  {Math.round(pct)}%
                </span>
              </div>
              <ProgressBar percent={pct} />
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const TierGroup = ({ slug, rewards, userTier, onRedeem }) => {
  if (!rewards.length) return null;
  const locked = tierIndex(userTier) < tierIndex(slug);
  return (
    <section data-testid={`rewards-group-${slug}`} className="mt-10 first:mt-0">
      <header className="flex items-baseline justify-between border-b border-napoli-gold/20 pb-3 mb-6">
        <div>
          <p className="font-flourish text-napoli-oxblood text-2xl leading-none">
            {tierLabel(slug)}
          </p>
          <p className="mt-1 font-body text-[10px] tracking-[0.28em] uppercase text-napoli-espresso-soft">
            {tierTagline(slug)}
          </p>
        </div>
        <p className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-espresso-soft">
          {rewards.length} reward{rewards.length === 1 ? "" : "s"}
          {locked ? " · locked" : ""}
        </p>
      </header>
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        data-testid={`rewards-grid-${slug}`}
      >
        {rewards.map((r) => (
          <RewardCard
            key={r.id}
            reward={r}
            userTier={userTier}
            onRedeem={onRedeem}
          />
        ))}
      </div>
    </section>
  );
};

const RewardsSkeleton = () => (
  <div data-testid="rewards-skeleton" className="px-6 md:px-10 py-8 max-w-7xl mx-auto">
    <SkeletonBlock className="h-3 w-32 mb-4" />
    <SkeletonBlock className="h-9 w-72 mb-2" />
    <SkeletonBlock className="h-3 w-56 mb-10" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonBlock key={i} className="h-72" />
      ))}
    </div>
  </div>
);

const CustomerRewardsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/customer/rewards");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail?.error || "load_failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    if (!data) return null;
    const buckets = TIER_ORDER.reduce((acc, t) => ({ ...acc, [t]: [] }), {});
    for (const r of data.rewards) {
      const t = (r.tier_required || "ospite").toLowerCase();
      if (buckets[t]) buckets[t].push(r);
      else buckets.ospite.push(r);
    }
    return buckets;
  }, [data]);

  const onRedeem = (reward) => {
    toast.success(`Show your QR — ${reward.name}`, {
      description:
        "Your server will scan and confirm your redemption tableside.",
      action: {
        label: "Open QR",
        onClick: () => navigate("/customer/qr"),
      },
      duration: 5000,
    });
  };

  if (err) {
    return (
      <div className="px-6 py-12 max-w-7xl mx-auto text-center font-body text-napoli-oxblood-dark">
        Couldn&rsquo;t load the rewards menu. Refresh in a moment.
      </div>
    );
  }
  if (!data || !grouped) return <RewardsSkeleton />;

  const totalRewards = data.rewards.length;
  const eligibleCount = data.rewards.filter((r) => r.eligible).length;
  const userTier =
    data.rewards.find((r) => r.eligible)?.tier_required || "ospite";
  const cheapest = [...data.rewards]
    .filter((r) => !r.eligible && r.reason_ineligible !== "tier_locked")
    .sort((a, b) => a.points_short - b.points_short)[0];

  return (
    <div data-testid="customer-rewards" className="px-6 md:px-10 py-8 md:py-12 max-w-7xl mx-auto">
      <div className="md:flex md:items-end md:justify-between gap-8">
        <div>
          <p className="font-flourish text-napoli-oxblood text-3xl">la ricompensa</p>
          <h1 className="mt-1 font-display font-bold text-3xl md:text-5xl text-napoli-espresso leading-tight">
            Earn it. Enjoy it.
          </h1>
          <p className="mt-3 font-body text-[13px] text-napoli-espresso-soft max-w-md">
            {totalRewards === 0
              ? "The catalog is being curated. Check back soon."
              : eligibleCount > 0
              ? `${eligibleCount} ready to redeem · ${data.user_balance.toLocaleString()} pts available.`
              : cheapest
              ? `Just ${cheapest.points_short} pts to your first reward — ${cheapest.name}.`
              : `${data.user_balance.toLocaleString()} pts available.`}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-3 mt-4 md:mt-0">
          <span className="inline-flex items-center gap-2 px-4 py-2 border border-napoli-gold/40 bg-napoli-marble">
            <span aria-hidden className="h-2 w-2 rounded-full bg-napoli-gold" />
            <span className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-espresso-soft">
              Balance
            </span>
            <span className="font-display font-bold text-base text-napoli-oxblood">
              {data.user_balance.toLocaleString()}
            </span>
          </span>
          <button
            type="button"
            onClick={() => navigate("/customer/qr")}
            data-testid="rewards-show-qr"
            className="inline-flex items-center gap-2 bg-napoli-espresso text-napoli-cream font-body font-semibold text-[11px] tracking-[0.18em] uppercase px-4 py-2 hover:bg-napoli-espresso-soft transition-colors"
          >
            <QrCode size={14} /> Show QR
          </button>
        </div>
      </div>

      {totalRewards === 0 ? (
        <div
          data-testid="rewards-empty"
          className="mt-10 text-center py-16 border border-dashed border-napoli-gold/30 bg-napoli-cream-warm/40"
        >
          <p className="font-flourish text-napoli-oxblood text-3xl">prossimamente</p>
          <p className="mt-3 font-body italic text-[14px] text-napoli-espresso-soft max-w-sm mx-auto">
            We&rsquo;re curating something worthy of your table. Check back soon.
          </p>
        </div>
      ) : (
        <div className="mt-8">
          {TIER_ORDER.map((slug) => (
            <TierGroup
              key={slug}
              slug={slug}
              rewards={grouped[slug] || []}
              userTier={userTier}
              onRedeem={onRedeem}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerRewardsPage;
